
import {
  Container,
  Dropdown,
  Segment,
  Table,
  Header,
  Button,
  Image,
} from 'semantic-ui-react'
import { Link } from "react-router-dom";
import moment from 'moment';

import { useQuery, useMutation, gql } from '@apollo/client';

import { ResponsiveContainer } from '../ResponsiveContainer';
import { useState, useLayoutEffect } from 'react';
import { GET_TAGS as GET_POSSIBLE_TAGS } from '../../queries/tags';
import { PATHS } from '../../paths';
import { useIsAdmin } from '../../hooks/isAdmin';

import styles from './Dashboard.module.css';

const CANCELLATION_REASONS = [
    'Teacher sick',
    'Teacher holiday',
    'Company cancelled',
    'Other'
];

const DELETE_SESSION = gql`
mutation Mutation($sessionId: Int!, $reason: String) {
  cancelSession(sessionId: $sessionId, reason: $reason) {
    id
  }
}`;

const GET_SESSIONS = gql`
query SessionsForDateAndTag($teacherId: String, $date: String!, $filters: String) {
  sessionsForDateAndTag(teacherId: $teacherId, date: $date, filters: $filters) {
    absentCount
    attendanceMarkingComplete
    attendancePercentage
    attendancePercentageNumerator
    attendancePercentageDenominator
    teacherMarkedAttendance
    startTime
    reasonForCancellation
    id
    course {
      name
      overseers {
        name
        id
        photoUrl
      }
      id
    }
  }
}`;

const Dashboard = () => {
    const isAdmin = useIsAdmin();
    // Read from local storage to get the date
  const [tagIds, setTagIds] = useState<string[]>(() => {
    const storedTags = localStorage.getItem('attendanceDashboardTags');
    return storedTags ? JSON.parse(storedTags) : [];
  })
  const [reasonForCancellation, setReasonForCancellation] = useState('Company cancelled');
  const [date, setDate] = useState(new Date());
  const [teacherId, setTeacherId] = useState("")
  const [deleteSession] = useMutation(DELETE_SESSION);
  const { loading, error, data, refetch } = useQuery(GET_SESSIONS, {
    variables: {
        filters: tagIds && tagIds.length > 0 ? tagIds.join(',') : "",
        teacherId: teacherId,
        date: date.toISOString().split('T')[0]
    }
  });

    const { loading: tagsLoading, error: tagsError, data: tagsData} = useQuery(GET_POSSIBLE_TAGS, {
        variables: {
            showPeopleTags: false,
            showCourseTags: true,
        }
    });

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useLayoutEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);


  if (error) {
      return <>Error</>
  }

     const teacherItem = (overseer:any) => {
        return (
            <>
                <Image avatar src={overseer.photoUrl} size='tiny' style={{ width: '2em', height: '2em', objectFit: 'cover' }}/>
                <Link to={`/people/${overseer.id}`}>
                    {overseer.name}
                </Link>
                <br />
            </>
        )
     }

     const renderRow = (session: any) => {
        const sessionStart = moment(Number(session.startTime));
        const isPast = sessionStart.isBefore(moment());
        return (
            <Table.Row className={isPast ? styles.pastSession : styles.upcomingSession} key={session.id}>
                <Table.Cell>{sessionStart.format('h:mm A')}</Table.Cell>
                <Table.Cell>
                    <Link to={`${PATHS.CLASSES}/${session.course.id}`}>
                        {session.course.name}
                    </Link>
                </Table.Cell>
                <Table.Cell>{session.reasonForCancellation ? 'Cancelled' : isPast && session.teacherMarkedAttendance ? Math.round(session.attendancePercentage*100) + '%': '❌'}</Table.Cell>
                <Table.Cell>{session.reasonForCancellation ? '-' : isPast && session.teacherMarkedAttendance ? session.absentCount : '-'}</Table.Cell>
                <Table.Cell>{session.reasonForCancellation ? '-' : isPast && session.attendanceMarkingComplete ? '✅': '❌'}</Table.Cell>
                <Table.Cell>{session.course.overseers.map(teacherItem) || '-'}</Table.Cell>
                <Table.Cell>{session.reasonForCancellation || '-'}</Table.Cell>
            </Table.Row>
        );
    };

    const realSessions = data && data.sessionsForDateAndTag.filter((s:any) => s.course != null);
    let overallAttendance = 0;
    let overallAbsent = 0;

    if (realSessions) {
        const num = realSessions.reduce((acc: any, value: any) => acc + value != null ? acc + value.attendancePercentageNumerator : 0, 0)
        const denum = realSessions.reduce((acc: any, value: any) => acc + value != null ? acc + value.attendancePercentageDenominator : 0, 0)
        if (denum > 0) {
            overallAttendance = Math.round((num / denum) * 100);
        }

        overallAbsent = realSessions.reduce((acc: any, value: any) => acc + value != null ? acc + value.absentCount : 0, 0)
    }


  return (
    <ResponsiveContainer>
      <Segment style={{ padding: '2em 0em' }} vertical>
        <Container>
            <Header as="h2" textAlign="center">
                {date.toISOString().split('T')[0] === new Date().toISOString().split('T')[0] ? "Today's" : date.toISOString().split('T')[0] } sessions
            </Header>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
            <Button
                primary
                onClick={async () => {
                    setDate(new Date(date.getTime() - (24 * 60 * 60 * 1000)))
                    await refetch({
                        filters: tagIds && tagIds.length > 0 ? tagIds.join(',') : "",
                        teacherId,
                        date: date.toISOString().split('T')[0]
                    })
                }}
            >
                {'<'}
            </Button>
            <Button
                primary
                onClick={async () => {
                    setDate(new Date())
                    await refetch({
                        filters: tagIds && tagIds.length > 0 ? tagIds.join(',') : "",
                        teacherId,
                        date: date.toISOString().split('T')[0]
                    })
                }}
            >
                today
            </Button>
            <Button
                primary
                onClick={async () => {
                    setDate(new Date(date.getTime() + (24 * 60 * 60 * 1000)))
                    await refetch({
                        filters: tagIds && tagIds.length > 0 ? tagIds.join(',') : "",
                        teacherId,
                        date: date.toISOString().split('T')[0]
                    })
                }}
            >
                {'>'}
            </Button>
      </div>
      <br />
        <Dropdown
            placeholder='Tags'
            fluid
            multiple
            value={tagIds}
            onChange={(_, { value }) => {
                localStorage.setItem('attendanceDashboardTags', JSON.stringify(value));
                setTagIds(value as string[])
                refetch({ teacherId, filters: value && (value as string[]).length > 0 ? (value as string[]).join(',') : ""})
            }}
            search
            selection
            options={
                tagsLoading || tagsError ? [] :
                (tagsData && tagsData.tags || [])
                .map((p: any) => ({
                    key: p.id,
                    text: p.name,
                    value: `tagId:${p.id}`,
                }))
            }
        />
        {data != null && data.sessionsForDateAndTag.length > 0 && (<Dropdown
            placeholder='[Beta] All Teachers (know issues with center mangers - be careful)'
            fluid
                                                                       value={Number(teacherId)}
            onChange={(_, { value }) => {
                setTeacherId(String(value))
                refetch({ filters: tagIds && tagIds.length > 0 ? tagIds.join(',') : "", teacherId: String(value) })
            }}
            selection
            options={
            data != null && data.sessionsForDateAndTag.reduce((acc: any, session: any) => {
                if (session.course) {
                    session.course.overseers.forEach((overseer: any) => {
                        if (!acc.find((item: any) => item.value === overseer.id)) {
                            acc.push({
                                key: overseer.id,
                                text: overseer.name,
                                value: overseer.id,
                            });
                        }
                    });
                }
                return acc;
            }, [{
                key: '',
                text: 'All Teachers',
                value: '',
            }]) || [{ key: '', text: 'All Teachers', value: '' }]
            }
                                                        />)}
      <br />
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                </div>
                {!isMobile && isAdmin &&
                 <>
                    <div></div><div>
                    <Dropdown
                        placeholder='Reason for cancellation'
                        selection
                        style={{ width: '200px' }}
                        value={reasonForCancellation}
                        onChange={(_, { value }) => {
                            if (CANCELLATION_REASONS.includes(value as string)) {
                                setReasonForCancellation(value as string)
                            }
                        }}
                        options={CANCELLATION_REASONS.map((reason) => ({
                            key: reason,
                            text: reason,
                            value: reason,
                        }))}
                    />
                    <Button
                        primary
                        style={{ marginLeft: '8px' }}
                        onClick={async () => {
                            if (window.confirm("Really cancel all the classes? (Use cancel to back out)")) {
                                for(let i = 0; i < data.sessionsForDateAndTag.length; i++) {
                                    const session = data.sessionsForDateAndTag[i]
                                    await deleteSession({ variables: { sessionId: session.id, reason: reasonForCancellation } })
                                }
                                await refetch({
                                    filters: tagIds && tagIds.length > 0 ? tagIds.join(',') : "",
                                    teacherId,
                                    date: date.toISOString().split('T')[0]
                                })
                            }
                        }
                    }
                    >
                        Cancel All Classes
                    </Button>
                     </div>
                </>
                }
            </div>
            {data && data.sessionsForDateAndTag.length > 0 ? (
                <Table celled compact>
                    {!isMobile &&
                <Table.Header>
                    <Table.Row>
                    <Table.HeaderCell>Start Time</Table.HeaderCell>
                    <Table.HeaderCell>Course</Table.HeaderCell>
                    <Table.HeaderCell>Attendance ({overallAttendance}%)</Table.HeaderCell>
                    <Table.HeaderCell>Absents ({overallAbsent})</Table.HeaderCell>
                    <Table.HeaderCell>Roll Complete</Table.HeaderCell>
                    <Table.HeaderCell>Overseers</Table.HeaderCell>
                    <Table.HeaderCell>Cancellation Reason</Table.HeaderCell>
                    </Table.Row>
                </Table.Header>}
                <Table.Body>
                    {data.sessionsForDateAndTag.filter((s:any) => s.course != null).map(renderRow)}
                    </Table.Body>
                </Table>
            ) : (
                <p>{loading ? 'Loading...' : 'No sessions scheduled for this date.'}</p>
            )}
        </Container>
      </Segment>
    </ResponsiveContainer>
  )
}

export { Dashboard }
